import Api, { ApiConfig } from '@/api/base/api'
import { TODAY_DELIVERY_PERFORMANCE_DAYS } from '@/constants/todayDelivery'
import { AxiosError } from 'axios'

/**
 * adminapi `domain/today_delivery/` — 오늘출발 셀러 API (배포 2b).
 *
 * 계약: hiver-dev-wiki `api-contract-배포2.md` §2 (v3.43) — **계약과 adminapi 구현이 같다**.
 *
 * `TODAY_DELIVERY_ENABLED=false` 면 모든 경로가 404 `TD_FEATURE_DISABLED` 다 —
 * 화면은 그 경우 오늘출발 UI 를 그리지 않는다(배포 1 동작과 동일).
 */

export type ProgramStatus = 'OFF' | 'ON' | 'SUSPENDED'
export type MigrationStatus = 'NONE' | 'PENDING' | 'RUNNING' | 'DONE'
export type DeliveryCode = '01' | '02'
export type DeliveryTypeCode = 'DS' | 'OS'
export type ApplyType = 'ALL' | 'PARTIAL' | 'NONE'
export type ExposureState = 'TODAY' | 'SOLDOUT' | 'NORMAL'
export type PerformanceStatus = 'COLLECTING' | 'READY'
export type SoldoutAction = 'SOLD_OUT' | 'CONVERT_NORMAL'

/**
 * 최근 30일 오늘출발 약속 이행률 (2b-E2).
 * 계약 `GET /v1/sellers/{md_no}/today-delivery/performance?days=30`.
 * `finalized_through`·`stats_at` 은 타입만 보존하고 화면에는 그리지 않는다.
 */
export interface TodayDeliveryPerformance {
  period_days: number
  period_start: string
  period_end_exclusive: string
  finalized_through: string | null
  promise_count: number
  kept_count: number
  kept_rate: number | null
  status: PerformanceStatus
  stats_at: string | null
}

export interface ReleaseAddress {
  release_zip_code: string | null
  release_address_1: string | null
  release_address_2: string | null
  release_tel_no: string | null
}

/** 흐름 띠 한 칸 — 양 끝 칸은 작은 라벨(`label`) + 굵은 본문(`text`), 가운데 칸(`arrow: true`)은 강조 글자(`label`) + 화살표. */
export interface PolicyAgreementFlowCell {
  label: string
  text?: string | null
  arrow?: boolean
}

/**
 * 동의 카드 안 그림 데이터 — adminapi `PolicyAgreementFigure`(신청 페이지 '10분 컷오프 연장 운영 기준' 절의 흐름 띠·예시 패널).
 * `flow` = [칸, 화살표, 칸] 3개(가운데만 `arrow`), `example` = 제목 + 행 1~4개. 행 `text` 는 `**…**` 강조 표기를 쓸 수 있다.
 * 자리표시자 치환은 강조 파싱보다 먼저 한다(`**{cutoff}까지**` → `**14:00까지**`).
 */
export interface PolicyAgreementFigure {
  type: 'cutoff_timeline'
  flow: PolicyAgreementFlowCell[]
  example: {
    title: string
    /** 셀러가 고른 마감시각이 없을 때 쓰는 기준 시각(HH:MM). 자리표시자를 쓰는 행이면 있어야 한다. */
    default_cutoff?: string | null
    /** `{before}`·`{after}` 가 기준 시각에서 떨어진 분(1~60 정수) */
    lag_minutes?: number | null
    /** 행 `time`·`text` 에는 자리표시자 `{cutoff}`·`{before}`·`{after}` 를 쓸 수 있다(화면이 고른 마감시각으로 바꾼다). */
    rows: { time: string; text: string; emphasis?: boolean }[]
  }
}

/** 그룹 카드 메타 — 문안 자체는 `items` 한 곳에만 있고 여기서는 index 로만 가리킨다(사본 금지, R-SADM-6). */
export interface PolicyAgreementGroup {
  title: string
  kind: 'OPERATION' | 'PENALTY'
  required: boolean
  item_indexes: number[]
  /** 선택 칸 — true 면 제목 옆에 '중요' 칩을 단다. */
  important?: boolean | null
  /** 선택 칸 — 있으면 카드 안에 흐름 띠·예시 패널을 그린다. 없거나(null) 모양이 어긋나면 그림 없이 문장만 그린다. */
  figure?: PolicyAgreementFigure | null
}

/** 동의 문안 — 서버 단일 원천(`today_delivery/policy_agreement.json`). 화면은 받아서 그리기만 한다. */
export interface PolicyAgreement {
  version: string
  page_url: string
  items: string[]
  /** 선택 메타 — 없거나 불완전하면 화면은 기존 평문으로 폴백한다. */
  groups?: PolicyAgreementGroup[]
}

export interface SellerPolicy extends ReleaseAddress {
  program_status: ProgramStatus
  order_cutoff_time: string | null
  cutoff_changed_at: string | null
  /** 지금 컷오프를 바꿀 수 있는지 — 마지막 변경 기준 30일에 1회(롤링, PM 2026-09-28) */
  cutoff_change_available: boolean
  /**
   * 컷오프를 다시 바꿀 수 있는 시각(마지막 변경 + 30일). 지금 바꿀 수 있으면 null.
   * adminapi 는 오프셋 없는 KST ISO 로 준다(`cutoff_changed_at` 과 같은 표기). 이 필드가 없는 구 응답도 있다.
   */
  cutoff_next_available_at?: string | null
  policy_agreed_version: string | null
  policy_agreed_at: string | null
  /** 코드 상수 — 동의 모달이 띄우는 문안 버전 */
  policy_version: string
  /** 동의 모달에 그대로 그릴 문안 (단일 원천) */
  policy_agreement: PolicyAgreement
  /** OFF→ON 저장에 동의가 필요한지 (이미 ON 이면 false) */
  agreement_required: boolean
  /** 참여 게이트(2b-A9) — false 면 ON 저장이 409 로 거절된다 */
  participation_allowed: boolean
  migration_status: MigrationStatus
  migration_deadline: string | null
  migrated_at: string | null
  suspended_at: string | null
  suspend_reason_type: string | null
  suspend_reason: string | null
  /**
   * 중지 직전 상태 — 중지(`SUSPENDED`)일 때만 값이 있고, 해제(RESTORE)가 되돌릴 상태와 같다(adminapi 2026-10-02).
   * 옛 서버·롤링 중 응답에는 이 필드가 없을 수 있다 — 없거나 null 이면 화면은 상태를 지어내지 않는다.
   */
  pre_suspend_status?: 'ON' | 'OFF' | null
  /** 신규 옵션이 복사해 가는 기본값. 없으면 화면은 SOLD_OUT. */
  default_soldout_action?: SoldoutAction | null
}

export interface SellerPolicyUpdate extends Partial<ReleaseAddress> {
  program_status: 'OFF' | 'ON'
  order_cutoff_time: string
  policy_agreed_version?: string | null
  /** 미전송이면 서버가 기존 값을 유지한다. */
  default_soldout_action?: SoldoutAction
}

/**
 * 항목별 독립 처리 결과 — 실패 행만 사유 코드와 함께 온다.
 *
 * 계약 §2(v3.43)·adminapi `today_delivery/schemas.FailedRow` 와 1:1 이다
 * (상품 지정이면 `product_no`, 단품 지정이면 `simple_product_no` 한쪽만 채워 온다).
 * 서버는 **문구를 주지 않으므로** 사유 코드 → 셀러 안내 변환은 화면 몫이다(`failureRowMessage`).
 */
export interface FailedRow {
  product_no?: number | null
  simple_product_no?: number | null
  reason_code: string
}

export interface DeliveryCodeChangeResult {
  success_count: number
  failures: FailedRow[]
}

export interface OptionItem {
  simple_product_no: number
  product_no: number
  product_name: string | null
  option_name: string | null
  /** 시안 ③ 컬럼 — 상품관리코드·썸네일·가격·진열/판매·등록일 */
  product_code: string | null
  /** 대표이미지 **상대 경로**(`/cproduct/…`) — 화면은 `utils/todayDelivery.optionImageUrl` 로 이미지 도메인을 붙인다 */
  image_url: string | null
  sell_price: number
  discount_price: number
  exhibit_yn: string | null
  sell_yn: string | null
  created_at: string | null
  dlvy_type_cd: DeliveryTypeCode | null
  delivery_code: DeliveryCode | null
  apply_type: ApplyType
  exposure_state: ExposureState
  stock_managed: boolean
  qty: number
  /** 재고가 0이 됐을 때 동작(`TODAY_SOLDOUT_ACTION`) — 구 응답엔 없을 수 있어 화면은 `normalizeSoldoutAction` 으로 읽는다 */
  soldout_action?: SoldoutAction | null
  /**
   * 그 옵션을 오늘출발로 추가하거나 변경한 일시(상품 수정일시 아님) — `created_at` 과 같은 직렬화로, 오프셋 없는 KST ISO(`2026-10-15T11:24:29`).
   * 기록이 없으면 null 이고, 이 필드가 없는 구 응답도 있다. 화면은 `YYYY-MM-DD HH:mm` 으로, 값이 없으면 '-' 로 그린다.
   */
  today_applied_at?: string | null
}

export interface OptionListResponse {
  list: OptionItem[]
  /** `include_summary=false`(페이지 이동) 요청이면 null — 화면은 직전 값을 그대로 쓴다 */
  total: number | null
  summary: { all_count: number; partial_count: number; none_count: number } | null
  policy_active: boolean
  on_holiday: boolean
}

/** 옵션 목록 조건 — 국내배송만 보여 주므로(서버 스코프 DS 고정, PM 2026-09-28) 배송구분(`dlvy_type_cd`) 조건은 없다. */
export interface OptionListParams {
  page?: number
  len?: number
  apply_type?: ApplyType | null
  exposure_state?: ExposureState | null
  /** 숫자 글자(1~18자리) — 16자리를 넘는 번호가 수로 바뀌며 정밀도를 잃지 않게 글자로 보낸다 */
  product_no?: string | null
  keyword?: string | null
  /** false = 요약 카드·건수를 다시 세지 않는다(페이지 이동 전용 — 셀러 전체 집계를 반복하지 않으려고) */
  include_summary?: boolean
}

/** [오늘출발 상품 추가하기] 상품 검색 구분 — 상품 리스트 필터의 검색구분과 같은 축. */
export type ProductSearchType = 'PRODUCT_NAME' | 'PRODUCT_NO' | 'PRODUCT_CODE' | 'MANAGE_CODE'

/**
 * 상품 검색 조건(adminapi 23b8f2d·666f162). `keyword` 는 필수 — 상품명(부분일치)·상품코드(정확일치)·상품관리코드(`MANAGE_CODE`,
 * 옵션의 `custom_sku` 정확일치)는 1~100자(`TD_INVALID_KEYWORD`),
 * `PRODUCT_NO` 는 공백·쉼표·줄바꿈으로 여러 개: 숫자가 아닌 조각 400 `TD_INVALID_PRODUCT_NOS`, 중복을 뺀 51개 이상 400 `TD_TOO_MANY_PRODUCTS`.
 * `len` 은 1~50(기본 20). 오류는 서버가 셀러 안내 문구(`meta.message`)를 함께 준다.
 */
export interface ProductSearchParams {
  search_type: ProductSearchType
  keyword: string
  page?: number
  len?: number
}

/** 상품 검색 결과 한 줄 — 옵션 관리 목록과 같은 범위(이 셀러의 판매·진열 국내배송 하이버 상품), 상품번호 내림차순. 이미 오늘출발인 상품도 나온다. */
export interface ProductSearchItem {
  product_no: number
  product_name: string | null
  product_code: string | null
  /** 목록(`OptionItem.image_url`)과 같은 규약 — 상대 경로면 `optionImageUrl` 로 이미지 도메인을 붙인다 */
  image_url: string | null
  /** 전체 옵션 수 / 그중 오늘출발 옵션 수 */
  option_count: number
  today_option_count: number
}

export interface ProductSearchResponse {
  list: ProductSearchItem[]
  total: number
}

/** 엑셀 일괄 지정 잡 상태 (2b-E1, 계약 §2 bulk-upload). */
export type BulkJobStatusCode = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED'

/** 업로드 접수 202 (`POST …/bulk-upload`, adminapi `BulkUploadAccepted`) — 잡 번호는 이 응답에서 받는다. */
export interface BulkUploadAccepted {
  job_id: string
  status: BulkJobStatusCode
}

/**
 * 진행 상태 (`GET …/bulk-upload/{job_id}`, adminapi `BulkJobStatus`).
 * `job_id` 는 **없을 수 있다** — 2026-10-01 현재 서버는 이 응답에 잡 번호를 싣지 않는다(추가 예정).
 * 화면은 이 값에 기대지 않고 자기가 아는 번호(업로드 응답·localStorage)를 붙인다(`BulkUploadJob`).
 */
export interface BulkUploadJobStatus {
  job_id?: string | null
  status: BulkJobStatusCode
  total?: number
  processed?: number
  success_count?: number
  fail_count?: number
  result_file_url?: string | null
  error_code?: string | null
  created_at?: string
  finished_at?: string | null
}

/** 화면이 들고 있는 잡 — 진행 상태에 **화면이 아는 잡 번호**를 붙인 것이라 `job_id` 가 항상 있다. */
export interface BulkUploadJob extends BulkUploadJobStatus {
  job_id: string
}

/**
 * 셀러 상품 목록의 배송구분 (계약 260928 §2) — 필터 항목과 같은 어휘.
 * `01` 일반배송 / `02` 오늘출발(전 옵션) / `02p` 오늘출발(일부 옵션).
 */
export type ProductDeliverCode = '01' | '02' | '02p'

/**
 * 셀러 상품 목록 엑셀 조건 (계약 260928 §2-1) — 전부 선택, 모두 AND.
 * `deliver_code` 말고는 v3 목록(`GET /v2/products`)에 보내던 값을 **그대로** snake_case 이름으로 옮긴다(변환 없음).
 */
export interface ProductDownloadParams {
  deliver_code?: ProductDeliverCode | null
  /** v3 `productName` — 상품명 부분일치 */
  keyword?: string | null
  /** v3 `productNos` — 선택 상품 또는 상품번호 검색(콤마, 최대 200) */
  product_nos?: number[] | null
  /** v3 `productSkus` — 상품코드 검색(콤마) */
  product_skus?: string[] | null
  /** v3 `exhibitionStatus` — `DISPLAY` / `NOT_DISPLAY` / `M_NOT_DISPLAY` */
  exhibition_status?: string | null
  /** v3 `sellStatus` — `SALE` / `NOT_SALE` / `M_NOT_SALE` */
  sell_status?: string | null
  /** v3 `isDiscount` — `false`(미할인)도 조건이다 */
  is_discount?: boolean | null
  /** v3 `deliveryType` — 국내 `DS` / 해외 `OS` */
  delivery_type?: DeliveryTypeCode | null
  category_no?: number | null
  brand_id?: string | null
  /** v3 `startDate`·`endDate` — 등록일 `YYYY-MM-DD` */
  start_date?: string | null
  end_date?: string | null
}

export interface ErrorData {
  policy_version?: string
  cutoff_changed_at?: string
  /** 컷오프 30일 1회 초과(`TD_CUTOFF_MONTHLY_LIMIT`) — 다시 바꿀 수 있는 시각(오프셋 없는 KST ISO, `cutoff_next_available_at` 과 같은 값) */
  next_available?: string
  /** 대상 SKU 합 상한 초과(`TD_TOO_MANY_OPTIONS`, 400) — 상한(40,000)과 이번 요청의 대상 SKU 수 */
  max_options?: number
  target_options?: number
  [key: string]: unknown
}

export interface ApiResponse<T> {
  meta: { code?: number; message?: string; error_code?: string; error_data?: ErrorData }
  data: T
}

/** adminapi 오류에서 `meta.error_code` / `meta.error_data` 를 꺼낸다 (`deliveryPolicyError` 와 같은 규약). */
export function todayDeliveryError(error: unknown) {
  const response = (error as AxiosError<ApiResponse<ErrorData>>)?.response
  const body = response?.data
  return {
    status: response?.status,
    error_code: body?.meta?.error_code,
    error_data: body?.meta?.error_data,
    message: body?.meta?.message,
  }
}

/**
 * `responseType: 'blob'` 요청의 오류 — 본문이 JSON 이 아니라 Blob 으로 와서 `todayDeliveryError` 로는 못 읽는다.
 * Blob 을 글자로 풀어 같은 모양으로 돌려준다(못 풀면 코드·문구 없이).
 */
export async function todayDeliveryBlobError(error: unknown) {
  const response = (error as AxiosError)?.response
  const body = response?.data
  if (typeof Blob === 'undefined' || !(body instanceof Blob)) return todayDeliveryError(error)
  try {
    const parsed = JSON.parse(await body.text()) as ApiResponse<ErrorData>
    return {
      status: response?.status,
      error_code: parsed?.meta?.error_code,
      error_data: parsed?.meta?.error_data,
      message: parsed?.meta?.message,
    }
  } catch (parseError) {
    return { status: response?.status, error_code: undefined, error_data: undefined, message: undefined }
  }
}

export default class TodayDeliveryApi extends Api {
  constructor(configs?: ApiConfig) {
    super({ baseURL: `${process.env.VUE_APP_SERVER_ENDPOINT_ADMINAPI}`, ...configs })
  }

  private base(sellerNo: number) {
    return `/v1/sellers/${sellerNo}/today-delivery`
  }

  /** `configs` — 상태만 확인하는 화면(옵션 관리·상품 수정)은 `silentError` 로 전역 오류 배너를 띄우지 않는다. */
  getPolicy(sellerNo: number, configs?: { silentError?: boolean }) {
    return this.requestGet<ApiResponse<SellerPolicy>>(`${this.base(sellerNo)}/policy`, undefined, configs)
  }

  putPolicy(sellerNo: number, body: SellerPolicyUpdate) {
    return this.requestPut<ApiResponse<SellerPolicy>>(`${this.base(sellerNo)}/policy`, body)
  }

  getOptions(sellerNo: number, params?: OptionListParams) {
    return this.requestGet<ApiResponse<OptionListResponse>>(`${this.base(sellerNo)}/options`, params)
  }

  /** 현재 필터에 맞는 옵션 목록(xlsx) 다운로드 — 양식·처리 결과 파일과 별개다. */
  getOptionsDownload(sellerNo: number, params?: Omit<OptionListParams, 'page' | 'len'>) {
    return this.requestGet<Blob>(`${this.base(sellerNo)}/options/download`, params, { responseType: 'blob' })
  }

  /** 상품·단품 배송코드 지정/해제 — 항목별 독립 처리(부분 실패 허용). */
  changeDeliveryCode(sellerNo: number, body: { product_nos?: number[]; simple_product_nos?: number[]; delivery_code: DeliveryCode }) {
    return this.requestPost<ApiResponse<DeliveryCodeChangeResult>>(`${this.base(sellerNo)}/products/delivery-code`, {
      product_nos: [],
      simple_product_nos: [],
      ...body,
    })
  }

  /** 상품 1건의 옵션(SKU) 배송타입 저장 — 옵션 관리·상품 등록/수정 옵션 편집(2b-S4)이 쓴다. */
  /** 줄의 `soldout_action` 은 그 줄이 새로 02 가 될 때만 서버가 쓴다 — 없으면 셀러 기본값 */
  saveProductOptions(
    sellerNo: number,
    productNo: number,
    options: { simple_product_no: number; delivery_code: DeliveryCode; soldout_action?: SoldoutAction }[]
  ) {
    return this.requestPut<ApiResponse<DeliveryCodeChangeResult>>(`${this.base(sellerNo)}/products/${productNo}/options`, {
      options,
    })
  }

  /**
   * [오늘출발 상품 추가하기] 상품 검색 — 이 셀러의 판매·진열 국내배송 하이버 상품.
   * 오류는 창 안에서 안내하므로 전역 배너를 띄우지 않는다(`silentError`).
   */
  searchProducts(sellerNo: number, params: ProductSearchParams) {
    return this.requestGet<ApiResponse<ProductSearchResponse>>(`${this.base(sellerNo)}/products/search`, params, { silentError: true })
  }

  /**
   * 오늘출발 옵션의 재고 소진 시 판매방식 동작 변경 — 항목별 독립 처리(부분 실패 허용), 결과는 배송타입 변경과 같은 모양이다.
   * **요청 1건에 값 하나 · 옵션 1~500개**(`SOLDOUT_ACTION_MAX_OPTIONS`) — 값이 섞였거나 더 많으면 호출부가 나눠 부른다.
   * 번호는 목록의 `simple_product_no`(옵션 없는 상품은 상품번호). 줄 거절 사유: `TD_NOT_OWNER` · `TD_NOT_TODAY`(오늘출발 옵션이 아님)
   * · `TD_NO_DETAIL_INFO`(상세 정보가 없어 일반배송 전환을 저장할 수 없음). 값이 같아 그대로 둔 줄도 성공으로 센다.
   */
  changeSoldoutAction(sellerNo: number, body: { simple_product_nos: number[]; soldout_action: SoldoutAction }) {
    return this.requestPut<ApiResponse<DeliveryCodeChangeResult>>(`${this.base(sellerNo)}/options/soldout-action`, body)
  }

  /** [2b-E1] 양식(xlsx) 다운로드. */
  getBulkUploadTemplate(sellerNo: number) {
    return this.requestGet<Blob>(`${this.base(sellerNo)}/products/bulk-upload/template`, undefined, { responseType: 'blob' })
  }

  /** [2b-E1] 엑셀 업로드 — 항상 비동기(202). 같은 파일 재요청은 기존 잡을 돌려준다(멱등). */
  postBulkUpload(sellerNo: number, file: File) {
    const form = new FormData()
    form.append('file', file)
    return this.requestPost<ApiResponse<BulkUploadAccepted>>(`${this.base(sellerNo)}/products/bulk-upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 2 * 60 * 1000,
    })
  }

  /** [2b-E1] 진행률 폴링 — 응답에 잡 번호가 없을 수 있다(`BulkUploadJobStatus`). */
  getBulkUploadJob(sellerNo: number, jobId: string) {
    return this.requestGet<ApiResponse<BulkUploadJobStatus>>(`${this.base(sellerNo)}/products/bulk-upload/${jobId}`)
  }

  /**
   * [2b-E1] 결과 파일(xlsx) 내려받기.
   * `result_file_url` 은 공개 URL 이 아니라 **인증이 필요한 adminapi 경로**라 링크가 아니라 이 호출로 받는다.
   * 보존 7일이 지나면 404 `TD_EXCEL_RESULT_EXPIRED`.
   */
  getBulkUploadResult(sellerNo: number, jobId: string) {
    return this.requestGet<Blob>(`${this.base(sellerNo)}/products/bulk-upload/${jobId}/result`, undefined, {
      responseType: 'blob',
    })
  }

  /**
   * [2b-E2] 최근 30일 오늘출발 약속 이행률.
   * 기능 플래그 OFF·403·네트워크 오류는 호출부가 블록만 숨긴다 — 본문 화면을 막지 않는다.
   * `silentError` 로 전역 배너·로딩을 건드리지 않는다.
   */
  getPerformance(sellerNo: number) {
    return this.requestGet<ApiResponse<TodayDeliveryPerformance>>(
      `${this.base(sellerNo)}/performance`,
      { days: TODAY_DELIVERY_PERFORMANCE_DAYS },
      { silentError: true }
    )
  }

  /**
   * [셀러 상품 목록] 엑셀(xlsx, 동기) — 배송구분 열이 있다. 최대 5,000행(초과 400 `TD_EXCEL_TOO_LARGE`).
   * 조건은 `ProductDownloadParams`(계약 260928 §2-1) — 서로 AND. `product_nos` 를 주면 그 상품만(콤마, 최대 200 —
   * 초과 400 `TD_TOO_MANY_PRODUCTS`), `product_skus` 도 콤마로 보낸다.
   * 오류 본문이 Blob 으로 오므로 호출부는 `todayDeliveryBlobError` 로 읽는다(`silentError`).
   */
  downloadProducts(sellerNo: number, params: ProductDownloadParams) {
    const { product_nos: productNos, product_skus: productSkus, ...conditions } = params
    const query = {
      ...conditions,
      ...(productNos ? { product_nos: productNos.join(',') } : {}),
      ...(productSkus ? { product_skus: productSkus.join(',') } : {}),
    }
    return this.requestGet<Blob>(`${this.base(sellerNo)}/products/download`, query, { responseType: 'blob', silentError: true })
  }
}
